/* eslint-disable camelcase */
import * as React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { FastField, Field, Formik } from "formik"
import { FormControl } from "../form-control/form-control"
import { Appearance, Dimensions, Platform, TextInput, TouchableOpacity, ViewStyle } from "react-native"
import { FormProps } from "./form"
import * as yup from "yup"
import { translate, TxKeyPath } from "../../i18n"
import _ from "lodash"
import { PhoneInput } from "../input/phone-input/phone-input"
import { PasswordStrengthMeterBar } from "../password-strength-meter-bar/password-strength-meter-bar"
import { Radio, useDisclose, View, Spinner } from "native-base"
import { Text } from "../text/text"
import { color } from "../../theme"
import { JalaIcon } from "../jala-icon/jala-icon"
import { PickerActionsheet } from "../picker-actionsheet/picker-actionsheet"
import { useStores } from "../../models"
import JalaAnalytic from "../../utils/jala-analytic"


const FORM_CONTROL: ViewStyle = {
  marginBottom: 4
}

const FORM_DROPDOWN: ViewStyle = {
  marginBottom: 4,
}

const PICKER_CONTAINER: ViewStyle = {
  borderWidth: 1,
  borderColor: color.palette.gray200,
  borderRadius: 5,
  padding: 8,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between"
}

const knowsFromWhere = [
  { code: "online_event" },
  { code: "offline_event" },
  { code: "social_media" },
  { code: "online_ads" },
  { code: "email" },
  { code: "blog_or_article" },
  { code: "offline_attribute" },
  { code: "friend_recomendation" },
  { code: "community" },
  { code: "other" }
]

export interface RegisterFormProps extends FormProps {
  initialValues?: {
    name?: string,
    phone?: string,
    email?: string,
    occupation?: string,
    state?: {
      know_jala_from?: string
    },
    password?: string,
    password_confirmation?: string
  }
}

/**
 * Describe your component here
 */
export const RegisterForm = observer(function RegisterForm(props: RegisterFormProps) {
  const { initialValues = {}, onSubmit, colorScheme = Appearance.getColorScheme(), innerRef, ...rest } = props
  const [ type, setType ] = useState<"Farmer"|"Non Farmer">()
  const { isOpen, onOpen, onClose } = useDisclose()
  const { isOpen: isOpenOccupation, onOpen: onOpenOccupation, onClose: onCloseOccupation } = useDisclose()
  const { occupationStore, occupationStore: { occupations, loading } } = useStores()

  const input = {
    name: useRef<TextInput>(),
    phone: useRef<TextInput>(),
    email: useRef<TextInput>(),
    password: useRef<TextInput>(),
    password_confirmation: useRef<TextInput>(),
  }

  const nextInput = {
    name: () => input.phone.current.focus(),
    phone: () => input.email.current.focus(),
    email: () => input.password.current.focus(),
    password: () => input.password_confirmation.current.focus(),
  }

  const validationSchema = yup.object({
    name: yup.string().ascii().required().label(translate("registerForm.name")),
    phone: yup.string().required().label(translate("registerForm.phone")),
    email: yup.string().email().label(translate("registerForm.email")),
    password: yup.string().ascii().zxcvbn(1).min(6).required().label(translate("registerForm.password")),
    password_confirmation: yup.string().ascii()
      .oneOf([yup.ref('password')], translate("registerForm.passwordConfirmationDoesntMatch"))
      .required().label(translate("registerForm.passwordConfirmation")),
    occupation: yup.string().ascii().nullable().required().label(translate("registerForm.type")),
    state: yup.object().shape({
      know_jala_from: yup.string().ascii().nullable().required().label(translate("registerForm.knowjalafrom")),
    }),
  })

  useEffect(() => {
    occupationStore.init();
  }, [occupationStore])

  const renderItemPicker = useCallback((item) => {
    return <View minHeight="44px" flexDirection="row" alignItems="center" px="16px">
      <Text text={item.item ? item.item.name_translated : "-"} preset="body2" color={color.palette.gray800} />
    </View>
  }, [])

  const occupationValue = useCallback((value) => {
    const occupation = occupations.length ? occupations.find((item) => item.code === value) : null
    return occupation?.name_translated || translate('registerForm.occupationPlaceholder')
  }, [occupations])

  return (
    <Formik
      initialValues={{
        name: '',
        phone: '',
        password: '',
        password_confirmation: '',
        ...initialValues
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ isValid, handleSubmit, setFieldValue }) => (
        <>
          <FastField name="name">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`registerForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  autoCapitalize: "words",
                  autoComplete: "name",
                  autoCorrect: false,
                  textContentType: "name",
                  keyboardType: "name-phone-pad",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `registerForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: handleChange(field.name),
                  onBlur: handleBlur(field.name),
                }}
                errorMessage={meta.error}
                style={FORM_CONTROL}
              />
            )}
          </FastField>
          <FastField name="phone">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`registerForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                errorMessage={meta.error}
                style={FORM_CONTROL}
              >
                <PhoneInput
                  colorScheme={colorScheme}
                  isInvalid={meta.touched && !!meta.error}
                  textProps={{
                    testID: `jala:id/${field.name}-input`,
                    ref: input[field.name],
                    autoCapitalize: "none",
                    autoComplete: "tel",
                    // autoCorrect: false,
                    textContentType: "telephoneNumber",
                    // keyboardType: "phone-pad",
                    returnKeyType: nextInput[field.name] ? "next" : "go",
                    onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                    placeholderTx: `registerForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                    defaultValue: meta.initialValue,
                    // onChangeText: handleChange(field.name),
                    onBlur: handleBlur(field.name)
                  }}
                  onChangePhoneNumber={handleChange(field.name)}
                />
              </FormControl>
            )}
          </FastField>
          <FastField name="email">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isInvalid={meta.touched && !!meta.error}
                labelTx={`registerForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  autoCapitalize: "none",
                  autoComplete: "email",
                  autoCorrect: false,
                  textContentType: "emailAddress",
                  keyboardType: "email-address",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `registerForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: (text) => handleChange(field.name)(text.trim()),
                  onBlur: handleBlur(field.name)
                }}
                errorMessage={meta.error}
                style={FORM_CONTROL}
              />
            )}
          </FastField>
          <FastField name="password">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`registerForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  type: "password",
                  autoCapitalize: "none",
                  autoComplete: "password",
                  autoCorrect: false,
                  textContentType: "newPassword",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `registerForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: handleChange(field.name),
                  onBlur: handleBlur(field.name)
                }}
                helperTextComponent={<PasswordStrengthMeterBar colorScheme={colorScheme} value={field.value} />}
                errorMessage={meta.error}
                style={FORM_CONTROL}
              />
            )}
          </FastField>
          <FastField name="password_confirmation">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`registerForm.${_.camelCase(field.name)}` as TxKeyPath}
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  type: "password",
                  autoCapitalize: "none",
                  autoComplete: "password",
                  autoCorrect: false,
                  textContentType: "password",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `registerForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: handleChange(field.name),
                  onBlur: handleBlur(field.name)
                }}
                errorMessage={meta.error}
                style={FORM_CONTROL}
              />
            )}
          </FastField>
          <FormControl
            labelTx="registerForm.type"
            colorScheme={colorScheme}
            labelColor={colorScheme === "light" ? color.palette.gray800 : color.palette.gray200}
            style={FORM_CONTROL}
            labelPreset="title3"
            isRequired
          >
            <Radio.Group
              colorScheme={colorScheme}
              name="type"
              accessibilityLabel={translate("registerForm.type")}
              value={type}
              onChange={(value: any) => {
                setType(value)
                setFieldValue('occupation', null)
              }}
            >
              <Radio value="Non Farmer" mr="32px" mb="8px">
                <Text tx="registerForm.nonFarmers" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
              <Radio value="Farmer">
                <Text tx="registerForm.farmers" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
            </Radio.Group>
          </FormControl>
          <Field
            key={`${type}-occupation`}
            name="occupation"
          >
            {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
              <FormControl
                isInvalid={meta.touched && !!meta.error}
                labelTx="registerForm.occupation"
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  autoCapitalize: "none",
                  autoCorrect: false,
                  defaultValue: meta.initialValue,
                }}
                errorMessage={meta.error}
                style={FORM_DROPDOWN}
                isRequired
                // {...formControlProps}
              >
                <TouchableOpacity style={{...PICKER_CONTAINER, backgroundColor: type ? color.palette.white : color.palette.gray100}} onPress={onOpenOccupation} disabled={loading || !type}>
                  <Text text={occupationValue(field.value)} color={field.value ? color.palette.gray800 : color.palette.gray500} preset="body2" />
                  {loading ? <Spinner /> : <JalaIcon name="expand_more" color={color.palette.gray800} size={24} />}
                </TouchableOpacity>
                <PickerActionsheet
                  colorScheme="light"
                  isOpen={isOpenOccupation}
                  onClose={onCloseOccupation}
                  data={occupations.filter((i) => i.type === type)}
                  titleTx="picker.choose"
                  withoutSearch
                  onPressItem={(item) => async () => {
                    await setFieldValue(field.name, item.code)
                    await setFieldTouched(field.name)
                    JalaAnalytic.log("Mobile App : Form Register - Occupation")
                  }}
                  renderItem={renderItemPicker}
                  flatListProps={{
                    keyExtractor: (item, index) => `${item.id || index}`,
                    estimatedItemSize: 44,
                  }}
                  // eslint-disable-next-line react-native/no-inline-styles
                  actionsheetStyle={{
                    minHeight: "40%",
                    ...(Platform.OS === 'web' ? { maxHeight: Dimensions.get("window").height * 2.5 } : {}),
                  }}
                />
              </FormControl>
            )}
          </Field>
          <Field name="state.know_jala_from">
            {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
              <FormControl
                isInvalid={meta.touched && !!meta.error}
                labelTx="registerForm.knowjalafrom"
                labelPreset="title3"
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  autoCapitalize: "none",
                  autoCorrect: false,
                  defaultValue: meta.initialValue,
                }}
                errorMessage={meta.error}
                style={FORM_DROPDOWN}
                isRequired
                // {...formControlProps}
              >
                <TouchableOpacity style={PICKER_CONTAINER} onPress={onOpen}>
                  <Text text={field.value ? translate(`registerForm.${field.value}` as TxKeyPath) : translate("picker.choose")} color={field.value ? color.palette.gray800 : color.palette.gray500} preset="body2" />
                  <JalaIcon name="expand_more" color={color.palette.gray800} size={24} />
                </TouchableOpacity>
                <PickerActionsheet
                  colorScheme="light"
                  isOpen={isOpen}
                  onClose={onClose}
                  data={knowsFromWhere}
                  titleTx="picker.choose"
                  withoutSearch
                  onPressItem={(item) => async () => {
                    await setFieldValue(field.name, item.code)
                    await setFieldTouched(field.name)
                    JalaAnalytic.log("Mobile App : Form Register - how you know jala")
                  }}
                  renderItem={(item) => (
                    <View minHeight="44px" flexDirection="row" alignItems="center" px="16px">
                      <Text text={item.item ? translate(`registerForm.${item.item.code}` as TxKeyPath) : "-"} preset="body2" color={color.palette.gray800} />
                    </View>
                  )}
                  flatListProps={{
                    keyExtractor: (item) => `${item.code}`,
                    estimatedItemSize: 44,
                  }}
                  // eslint-disable-next-line react-native/no-inline-styles
                  actionsheetStyle={{
                    minHeight: "40%",
                    ...(Platform.OS === 'web' ? { maxHeight: Dimensions.get("window").height * 2.5 } : {}),
                  }}
                />
              </FormControl>
            )}
          </Field>
        </>
      )}
    </Formik>
  )
})

RegisterForm.displayName = 'RegisterForm'
